import React, { useState, useCallback } from 'react';
import { PageContainer } from '../components/common/layout/PageContainer';
import { ContactFormModal } from '../components/features/contacts/ContactFormModal';
import { ContactTable } from '../components/features/contacts/ContactTable';
import { ContactTableHeader } from '../components/features/contacts/ContactTableHeader';
import { AddTagsModal } from '../components/features/contacts/AddTagsModal';
import { useContacts as useContactsData } from '../hooks/graphql/contacts/useContacts';
import { OffsetPagination } from '../components/common/OffsetPagination';
import { TableCard } from '../components/common/TableCard';
import { EditContactModal } from '../components/features/contacts/EditContactModal';
import { useAuthStore } from '../contexts/auth/useAuthStore';
import { UploadDatabaseModal } from '../components/features/flows/UploadDatabaseModal';
import { useHasIntegration } from '../hooks/useHasIntegration';
import type { ContactFormData } from '../schemas/contact.schema';

const Contact: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{ id: string } | null>(null);
  const [showUploadDatabaseModal, setShowUploadDatabaseModal] = useState(false);
  useAuthStore();
  const hasIntegration = useHasIntegration();

  const _handleOpenEditModal = (contact: { id: string }) => {
    // Just pass the contact ID to the EditContactModal
    // The EditContactModal will fetch the full contact data itself
    setSelectedContact({ id: contact.id });
    setShowEditModal(true);
  };

  const {
    contacts,
    isLoading,
    error,
    totalContacts,
    searchQuery,
    searchField = '',
    selectedContacts,
    sortField,
    sortDirection,
    pageSize,
    currentPage,
    handleSelectAll,
    handleSelectContact,
    handleSort,
    handleSearch,
    handleDownload,
    handleAddContact,
    handleDeleteContact,
    handleDeleteSelected,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handlePageSizeChange,
    handleFilter,
    refetch,
  } = useContactsData();

  const handleEdit = useCallback((_contact: { id: string }) => {
    // TODO: Implement edit logic
  }, []);

  const handleSubmit = useCallback(
    async (data: ContactFormData) => {
      const success = await handleAddContact(data);
      if (success) {
        setIsModalOpen(false);
      }
    },
    [handleAddContact]
  );

  // Show other errors
  if (error instanceof Error) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-red-600">Error loading contacts: {error.message}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="py-6 max-w-[1700px] 3xl:max-w-[2100px]">
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col gap-6">
          <ContactTableHeader
            searchQuery={searchQuery}
            searchField={searchField}
            onSearchChange={handleSearch}
            onRefresh={refetch}
            onDownload={handleDownload}
            onAddContact={() => setIsModalOpen(true)}
            onUploadDatabase={!hasIntegration ? () => setShowUploadDatabaseModal(true) : undefined}
            onFilter={handleFilter}
            isLoading={isLoading}
            totalContacts={totalContacts}
          />

          <TableCard
            className="overflow-x-clip"
            footer={
              <OffsetPagination
                currentPage={currentPage ?? 0}
                totalItems={totalContacts}
                pageSize={pageSize}
                onNextPage={handleNextPage}
                onPrevPage={handlePrevPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isLoading={isLoading}
                label="contacts"
              />
            }
          >
            <ContactTable
              contacts={contacts}
              isLoading={isLoading}
              selectedContacts={selectedContacts}
              onSelectAll={handleSelectAll}
              onSelectContact={handleSelectContact}
              onEdit={handleEdit}
              onDelete={handleDeleteContact}
              onDeleteSelected={handleDeleteSelected}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortDirection}
              onAddTags={() => setIsAddTagsModalOpen(true)}
            />
          </TableCard>
        </div>
      </div>

      <ContactFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
      />

      <AddTagsModal
        isOpen={isAddTagsModalOpen}
        onClose={() => setIsAddTagsModalOpen(false)}
        selectedContacts={selectedContacts}
        onSuccess={refetch}
      />

      <EditContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        contact={selectedContact}
        onSubmit={async (_data) => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        onSuccess={(_data) => {
          // Refresh contacts data after successful edit
          refetch();
        }}
      />

      {/* Upload Database Modal */}
      <UploadDatabaseModal
        isOpen={showUploadDatabaseModal}
        onClose={() => setShowUploadDatabaseModal(false)}
      />
    </PageContainer>
  );
};

export default Contact;
